/**
 * Work → Records and data: what's waiting (imports, questions, partner answers), files sent to
 * partners, verification programs, evidence packets and downloads, all in one place.
 */
import { useState } from "react";
import { Route, Routes, useNavigate, useParams, useSearchParams } from "react-router";
import type { JobSummary } from "../../../contract";
import { api, qs } from "../../../web/lib/api";
import { formatDate, relativeDay, timeAgo, useFormat } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan, useRanchToday } from "../../../web/lib/session";
import { RefPicker } from "../../../web/forms/pickers";
import { eventSummary } from "../../../web/shell/describe";
import { Page } from "../../../web/shell/Layout";
import { useCaptureSubject } from "../../../web/shell/Composer";
import { Button, Card, Chip, Empty, ErrorNote, Field, Icon, List, Loading, PageHeader, Progress, Row, Section, Select, Sheet, Stat, TagLabel, TextArea, TextInput, toast } from "../../../web/ui/kit";
import type { AdapterDTO, EvidencePacket, ExchangeDTO, ExportDTO, ImportSummary } from "../shared/types";
import { fileUrl, postForm, tm } from "./api";
import { EXCHANGE_STATUS, IMPORT_STATUS, KIND_LABEL, StatusChip } from "./labels";

export function DataWorkspace() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="exchanges/:id" element={<ExchangePage />} />
      <Route path="evidence" element={<EvidencePage />} />
      <Route path="*" element={<Overview />} />
    </Routes>
  );
}

interface OverviewDTO { imports: ImportSummary[]; issues: number; exports: ExportDTO[]; exchanges: ExchangeDTO[]; programs: JobSummary[] }

function Overview() {
  useLanguage();
  const can = useCan();
  const navigate = useNavigate();
  const q = useApi<OverviewDTO>("/m/data/overview");
  const [preparing, setPreparing] = useState(false);
  const [starting, setStarting] = useState(false);
  const today = useRanchToday();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <Page><ErrorNote error={q.error} retry={() => q.refetch()} /></Page>;
  const d = q.data;
  const answered = d.exchanges.filter((e) => e.status === "rejected" || (e.status === "partial" && e.counts.rejected));
  const waiting = d.imports.length + d.issues + answered.length;
  return (
    <Page>
      <PageHeader back={{ to: "/work", label: t("Work") }} title={t("Records and data")} subtitle={t("Bring in spreadsheets and old records, download everything, send files to partners and prepare evidence packets.")} />
      {waiting > 0 && (
        <Section title={t("Needs you")}>
          <List>
            {d.imports.map((i) => <Row key={i.id} icon="upload" tone="info" to={`/ranch/data/imports/${i.id}`} title={i.title} meta={plural(i.rows, "{{count}} row", "{{count}} rows")} trailing={<StatusChip map={IMPORT_STATUS} value={i.status} />} />)}
            {d.issues > 0 && <Row icon="warn" tone="warn" to="/ranch/data?tab=questions" title={plural(d.issues, "{{count}} import question to settle", "{{count}} import questions to settle")} />}
            {answered.map((e) => <Row key={e.id} icon="send" tone="bad" to={`/work/data/exchanges/${e.id}`} title={t("{{title}}: {{count}} not accepted", { title: e.title, count: e.counts.rejected })} meta={e.contact?.label} />)}
          </List>
        </Section>
      )}
      <div className="data-actions-grid">
        {can("data") && <Button size="lg" icon="upload" to="/ranch/data">{t("Bring in records")}</Button>}
        {can("data") && <Button size="lg" icon="download" to="/ranch/data?tab=download">{t("Download everything")}</Button>}
        {can("data") && <Button size="lg" icon="send" onClick={() => setPreparing(true)}>{t("Prepare a partner file")}</Button>}
        <Button size="lg" icon="shield" to="/work/data/evidence">{t("Evidence packet")}</Button>
        {can("record") && <Button size="lg" icon="award" onClick={() => setStarting(true)}>{t("Start a verification program")}</Button>}
      </div>
      <Section title={t("Partner files")} count={d.exchanges.length} hint={t("Prepared isn't sent, and sent isn't accepted. Each file keeps its partner's answer, animal by animal.")}>
        {d.exchanges.length ? <List>{d.exchanges.map((e) => (
          <Row key={e.id} icon="send" to={`/work/data/exchanges/${e.id}`} title={e.title}
            meta={[e.contact?.label, plural(e.counts.total, "{{count}} animal", "{{count}} animals"), e.sent_at ? t("sent {{when}}", { when: relativeDay(e.sent_at, today) }) : null].filter(Boolean).join(" · ")}
            trailing={<StatusChip map={EXCHANGE_STATUS} value={e.status} />} />
        ))}</List> : <p className="muted">{t("No partner files waiting on anyone.")}</p>}
      </Section>
      <Section title={t("Verification programs")} count={d.programs.length}>
        {d.programs.length ? <List>{d.programs.map((j) => (
          <Row key={j.id} icon="award" to={`/work/jobs/${j.id}`} title={j.title} meta={[j.stage ? t(j.stage === "preparing" ? "Gathering evidence" : j.stage === "submitted" ? "Sent to the provider" : "Provider decided") : null, plural(j.progress.total, "{{count}} animal", "{{count}} animals")].filter(Boolean).join(" · ")}
            trailing={j.status !== "active" ? <Chip tone="neutral">{t(j.status === "done" ? "Finished" : j.status === "cancelled" ? "Cancelled" : "Paused")}</Chip> : undefined} />
        ))}</List> : <p className="muted">{t("No programs yet. Start one when a buyer or program asks for source, age or treatment evidence.")}</p>}
      </Section>
      {d.exports.length > 0 && (
        <Section title={t("Recent downloads")}>
          <List>{d.exports.map((e) => <Row key={e.id} icon="download" title={e.name} meta={[formatDate(e.created_at), e.created_by?.label].filter(Boolean).join(" · ")} trailing={<a className="btn btn-ghost btn-sm" href={fileUrl(`/exports/${e.id}/file`)}>{t("Download")}</a>} />)}</List>
        </Section>
      )}
      {preparing && <PrepareSheet onClose={() => setPreparing(false)} onDone={(id) => navigate(`/work/data/exchanges/${id}`)} />}
      {starting && <StartProgramSheet onClose={() => setStarting(false)} onDone={(id) => navigate(`/work/jobs/${id}`)} />}
    </Page>
  );
}

export function PrepareSheet({ onClose, onDone, initial }: { onClose: () => void; onDone: (id: string) => void; initial?: { adapter?: string; contact_id?: string | null; job_id?: string; animal_ids?: string[]; title?: string } }) {
  const adapters = useApi<{ items: AdapterDTO[] }>("/m/data/adapters");
  const [adapter, setAdapter] = useState(initial?.adapter ?? "");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [contact, setContact] = useState<string | null>(initial?.contact_id ?? null);
  const [group, setGroup] = useState<string | null>(null);
  const [animals, setAnimals] = useState<string[]>(initial?.animal_ids ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const chosen = adapters.data?.items.find((a) => a.id === adapter);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const e = await api.post<ExchangeDTO>("/m/data/exchanges", { adapter, title: title || undefined, contact_id: contact, job_id: initial?.job_id ?? null, group_id: group, animal_ids: animals });
      await invalidateAll();
      onDone(e.id);
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={t("Prepare a partner file")} footer={<Button variant="primary" size="lg" block busy={busy} disabled={!adapter || (!group && !animals.length)} onClick={() => void go()}>{t("Prepare it")}</Button>}>
      <Field label={t("What kind of file")}>
        <Select value={adapter} onChange={(e) => setAdapter(e.target.value)}>
          <option value="">{t("Choose…")}</option>
          {(adapters.data?.items ?? []).map((a) => <option key={a.id} value={a.id}>{t(a.label)}</option>)}
        </Select>
      </Field>
      {chosen && <p className="muted">{t(chosen.description)}</p>}
      <Field label={t("Name")} optional><TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("October calves for the feedlot")} /></Field>
      <Field label={t("Who it's for")} optional><RefPicker target="contact" value={contact} onChange={(v) => setContact(v as string | null)} /></Field>
      {!initial?.animal_ids?.length && <>
        <Field label={t("Which cattle")} hint={t("The animals are fixed when you prepare the file.")}><RefPicker target="group" value={group} onChange={(v) => setGroup(v as string | null)} placeholder={t("A group")} /></Field>
        <Field label={t("Or pick animals")} optional><RefPicker target="animal" multiple value={animals} onChange={(v) => setAnimals((v as string[]) ?? [])} /></Field>
      </>}
      {error ? <ErrorNote error={error} /> : null}
    </Sheet>
  );
}

function StartProgramSheet({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [program, setProgram] = useState("");
  const [provider, setProvider] = useState<string | null>(null);
  const [group, setGroup] = useState<string | null>(null);
  const [claims, setClaims] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const g = group ? await api.get<{ name: string; members: Array<{ id: string }> }>(`/groups/${group}`) : null;
      const { id } = await api.post<{ id: string }>("/jobs", {
        type: "data.program", title: g ? `${program}: ${g.name}` : program, group_id: group, stage: "preparing",
        data: { program, ...(provider ? { provider_id: provider } : {}), ...(claims ? { claims } : {}) }, subjects: (g?.members ?? []).map((m) => ({ kind: "animal", id: m.id })),
      });
      await invalidateAll();
      onDone(id);
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={t("Start a verification program")} footer={<Button variant="primary" size="lg" block busy={busy} disabled={!program.trim() || !group} onClick={() => void go()}>{t("Start")}</Button>}>
      <Field label={t("Program")} hint={t("The program's own name, like a source and age program.")}><TextInput value={program} onChange={(e) => setProgram(e.target.value)} placeholder={t("Source and age")} /></Field>
      <Field label={t("Which cattle")} hint={t("The group's animals today become the program's list. Later changes to the group don't change it.")}><RefPicker target="group" value={group} onChange={(v) => setGroup(v as string | null)} /></Field>
      <Field label={t("Provider")} optional><RefPicker target="contact" value={provider} onChange={(v) => setProvider(v as string | null)} /></Field>
      <Field label={t("What it checks")} optional><TextArea rows={3} value={claims} onChange={(e) => setClaims(e.target.value)} placeholder={t("Born here, calving dates, EID on every calf")} /></Field>
      {error ? <ErrorNote error={error} /> : null}
    </Sheet>
  );
}

// ---------------------------------------------------------------- one exchange

function ExchangePage() {
  useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const q = useApi<ExchangeDTO>(`/m/data/exchanges/${id}`);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [marking, setMarking] = useState(false);
  const [choices, setChoices] = useState<Record<string, { outcome: "accepted" | "rejected"; reason?: string }>>({});
  useCaptureSubject(q.data ? { screen: `exchange:${q.data.id}` } : null);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <Page><ErrorNote error={q.error} /></Page>;
  const e = q.data;
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try { await fn(); await q.refetch(); await invalidateAll(); } catch (err) { setError(err); } finally { setBusy(null); }
  };
  const reply = async (file: File | undefined) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file, file.name);
    await run("reply", () => postForm(`/exchanges/${e.id}/answer`, form));
  };
  const saveMarks = () => run("rows", () => api.post(`/m/data/exchanges/${e.id}/answer`, { rows: Object.entries(choices).map(([key, c]) => ({ key, outcome: c.outcome, reason: c.reason })), reference: reference || undefined }).then(() => setMarking(false)));
  const answerable = ["prepared", "sent", "unknown", "partial", "rejected", "acknowledged"].includes(e.status);
  return (
    <Page>
      <PageHeader back={{ to: "/work/data", label: t("Records and data") }} eyebrow={t(e.adapter.label)} title={e.title}
        subtitle={[e.contact?.label, t(KIND_LABEL[e.kind] ?? e.kind), plural(e.counts.total, "{{count}} animal", "{{count}} animals")].filter(Boolean).join(" · ")}>
        <StatusChip map={EXCHANGE_STATUS} value={e.status} />
      </PageHeader>
      {e.superseded_by && <Card tone="info"><p>{t("Replaced by a newer version:")} <a href={`/work/data/exchanges/${e.superseded_by.id}`} onClick={(ev) => { ev.preventDefault(); navigate(`/work/data/exchanges/${e.superseded_by!.id}`); }}>{e.superseded_by.label}</a></p></Card>}
      {e.supersedes && <p className="muted">{t("This replaces an earlier file. That file and its answer are kept.")}</p>}
      <div className="data-stats">
        <Stat label={t("In the file")} value={e.counts.total} />
        <Stat label={t("Accepted")} value={e.counts.accepted} tone={e.counts.accepted ? "good" : undefined} />
        <Stat label={t("Not accepted")} value={e.counts.rejected} tone={e.counts.rejected ? "bad" : undefined} />
        <Stat label={t("No answer yet")} value={e.counts.pending} />
      </div>
      {e.file && (
        <Card>
          <p><Icon name="file" size={16} /> <strong>{e.file.name}</strong> · {t("prepared {{when}}", { when: timeAgo(e.created_at) })}{e.reference ? ` · ${t("reference {{ref}}", { ref: e.reference })}` : ""}</p>
          <div className="row-actions">
            <a className="btn btn-secondary btn-lg" href={fileUrl(`/exchanges/${e.id}/file`)}><Icon name="download" size={18} /><span>{t("Download the file")}</span></a>
            {e.response && <a className="btn btn-ghost" href={fileUrl(`/exchanges/${e.id}/file?which=response`)}><Icon name="download" size={18} /><span>{t("Their reply")}</span></a>}
          </div>
        </Card>
      )}
      {e.status === "prepared" && (
        <Card>
          <h3>{t("Sent it?")}</h3>
          <p className="muted">{t("Download the file and send it the way your partner takes it. Then mark it sent so you know to watch for their answer.")}</p>
          <Field label={t("Their reference number")} optional><TextInput value={reference} onChange={(ev) => setReference(ev.target.value)} /></Field>
          <div className="row-actions"><Button variant="primary" size="lg" icon="send" busy={busy === "sent"} onClick={() => void run("sent", () => api.post(`/m/data/exchanges/${e.id}/sent`, { reference: reference || null }))}>{t("Mark as sent")}</Button></div>
        </Card>
      )}
      {answerable && e.status !== "prepared" && e.counts.pending > 0 && (
        <Card>
          <h3>{t("What did they say?")}</h3>
          <div className="data-row-actions">
            <Button variant="primary" busy={busy === "all"} onClick={() => void run("all", () => api.post(`/m/data/exchanges/${e.id}/answer`, { all: "accepted" }))}>{t("They accepted the rest")}</Button>
            <label className="btn btn-secondary"><Icon name="upload" size={18} /><span>{t("Upload their reply")}</span><input type="file" hidden accept=".csv,.txt,.tsv" onChange={(ev) => void reply(ev.target.files?.[0])} /></label>
            <Button onClick={() => setMarking(true)}>{t("Mark animals one by one")}</Button>
            {e.status === "sent" && <Button variant="ghost" busy={busy === "unknown"} onClick={() => void run("unknown", () => api.post(`/m/data/exchanges/${e.id}/unknown`, {}))}>{t("No word back")}</Button>}
          </div>
        </Card>
      )}
      {e.counts.rejected > 0 && e.status !== "superseded" && (
        <Card tone="warn">
          <p>{plural(e.counts.rejected, "{{count}} animal wasn't accepted. Fix what they asked for, then send a corrected file.", "{{count}} animals weren't accepted. Fix what they asked for, then send a corrected file.")}</p>
          <div className="row-actions"><Button variant="primary" busy={busy === "redo"} onClick={() => void run("redo", async () => { const n = await api.post<ExchangeDTO>(`/m/data/exchanges/${e.id}/redo`, { which: "rejected" }); navigate(`/work/data/exchanges/${n.id}`); })}>{t("Prepare a corrected file")}</Button></div>
        </Card>
      )}
      {error ? <ErrorNote error={error} /> : null}
      <Section title={t("Animals")}>
        <List>{e.rows.map((r) => (
          <Row key={r.key} leading={<TagLabel tag={r.label} />} title={r.key !== r.label ? r.key : ""}
            meta={[r.reason, ...(r.issues ?? []).map(tm)].filter(Boolean).join(" · ")}
            trailing={<Chip tone={r.outcome === "accepted" || r.outcome === "revised" ? "good" : r.outcome === "rejected" ? "bad" : "neutral"}>{t(r.outcome === "accepted" ? "Accepted" : r.outcome === "revised" ? "Accepted with changes" : r.outcome === "rejected" ? "Not accepted" : "Waiting")}</Chip>} />
        ))}</List>
      </Section>
      <Sheet open={marking} onClose={() => setMarking(false)} title={t("Mark animals one by one")} size="lg" footer={<Button variant="primary" size="lg" block busy={busy === "rows"} disabled={!Object.keys(choices).length} onClick={() => void saveMarks()}>{t("Save their answer")}</Button>}>
        <ul className="data-files">{e.rows.filter((r) => r.outcome === "pending").map((r) => (
          <li key={r.key} className="data-file">
            <span className="data-file-name"><TagLabel tag={r.label} /></span>
            <Button size="sm" variant={choices[r.key]?.outcome === "accepted" ? "good" : "secondary"} onClick={() => setChoices((c) => ({ ...c, [r.key]: { outcome: "accepted" } }))}>{t("Accepted")}</Button>
            <Button size="sm" variant={choices[r.key]?.outcome === "rejected" ? "danger" : "secondary"} onClick={() => setChoices((c) => ({ ...c, [r.key]: { outcome: "rejected", reason: c[r.key]?.reason } }))}>{t("Not accepted")}</Button>
            {choices[r.key]?.outcome === "rejected" && <TextInput value={choices[r.key]?.reason ?? ""} placeholder={t("Why")} aria-label={t("Why")} onChange={(ev) => setChoices((c) => ({ ...c, [r.key]: { outcome: "rejected", reason: ev.target.value } }))} />}
          </li>
        ))}</ul>
      </Sheet>
    </Page>
  );
}

// ---------------------------------------------------------------- evidence packet

export function EvidenceView({ packet }: { packet: EvidencePacket }) {
  const fmt = useFormat(packet.labels);
  return (
    <div className="stack">
      <div className="data-stats">
        <Stat label={t("Animals")} value={packet.coverage.total} />
        <Stat label={t("Complete")} value={packet.coverage.complete} tone={packet.coverage.complete === packet.coverage.total ? "good" : undefined} />
        <Stat label={t("With an EID")} value={packet.coverage.with_eid} />
        <Stat label={t("With a birth date")} value={packet.coverage.with_birth} />
        <Stat label={t("Bought in")} value={packet.coverage.purchased} />
        <Stat label={t("On withdrawal")} value={packet.coverage.on_hold} tone={packet.coverage.on_hold ? "warn" : undefined} />
      </div>
      {packet.animals.map((a) => (
        <Card key={a.id} className="data-evidence-animal">
          <div className="data-animal-head">
            <TagLabel tag={a.tag ?? a.label} size="lg" />
            {a.eid ? <span className="mono">{a.eid}</span> : null}
            {a.gaps.map((g, i) => <Chip key={i} tone="warn">{tm(g)}</Chip>)}
            {a.adverse.map((g, i) => <Chip key={`a${i}`} tone="bad">{tm(g)}</Chip>)}
          </div>
          <p className="muted">{[a.birth.date ? t("born {{date}}", { date: formatDate(a.birth.date, a.birth.precision ?? "date") }) : t("birth date not recorded"), a.origin ? t(a.origin === "born" ? "Born here" : a.origin === "purchased" ? "Purchased" : a.origin === "received" ? "Received" : a.origin === "imported" ? "Imported" : "Unknown") : null,
            a.arrival?.on ? t("arrived {{date}}", { date: formatDate(a.arrival.on) }) : null, a.dam ? t("dam {{tag}}", { tag: a.dam }) : null].filter(Boolean).join(" · ")}</p>
          {a.identifiers.filter((i) => i.to).length > 0 && <p className="muted">{t("Earlier IDs: {{ids}}", { ids: a.identifiers.filter((i) => i.to).map((i) => `${i.value} (${formatDate(i.to)})`).join(", ") })}</p>}
          {a.moves.length > 0 && <><strong>{t("Moves")}</strong><ol>{a.moves.map((m) => <li key={m.event_id}>{m.on ? formatDate(m.on, m.precision) : t("Unknown date")}: {eventSummary({ type: m.type, data: m.data, subjects: m.subjects, occurred_at: m.on }, fmt)}</li>)}</ol></>}
          {a.treatments.length > 0 && <><strong>{t("Treatments")}</strong><ol>{a.treatments.map((m) => (
            <li key={m.event_id}>{m.on ? formatDate(m.on, m.precision) : t("Unknown date")}: {eventSummary({ type: m.type, data: m.data, subjects: m.subjects, occurred_at: m.on }, fmt)}
              {m.withdrawal_until ? ` · ${m.clear ? t("withdrawal ended {{date}}", { date: formatDate(m.withdrawal_until) }) : t("on withdrawal until {{date}}", { date: formatDate(m.withdrawal_until) })}` : ""}</li>
          ))}</ol></>}
        </Card>
      ))}
    </div>
  );
}

function EvidencePage() {
  useLanguage();
  const [params, setParams] = useSearchParams();
  const can = useCan();
  const group = params.get("group");
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const set = (k: string, v: string | null) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const query = qs({ group, from, to });
  const q = useApi<EvidencePacket>(group ? `/m/data/evidence${query}` : null);
  return (
    <Page>
      <PageHeader back={{ to: "/work/data", label: t("Records and data") }} title={t("Evidence packet")}
        subtitle={t("IDs, birth dates, origins, moves and treatments with withdrawal status. It shows what your records say; only a program's provider can verify it.")}
        actions={q.data ? <span className="row-actions data-noprint"><Button icon="file" onClick={() => window.print()}>{t("Print")}</Button>{can("data") && <a className="btn btn-secondary" href={fileUrl(`/evidence.csv${query}`)}><Icon name="download" size={18} /><span>{t("Spreadsheet")}</span></a>}</span> : undefined} />
      <Card className="data-noprint">
        <Field label={t("Which cattle")}><RefPicker target="group" value={group} onChange={(v) => set("group", (v as string | null) ?? null)} /></Field>
        <div className="filter-row">
          <Field label={t("From")} optional><TextInput type="date" value={from} onChange={(e) => set("from", e.target.value)} /></Field>
          <Field label={t("To")} optional><TextInput type="date" value={to} onChange={(e) => set("to", e.target.value)} /></Field>
        </div>
      </Card>
      {!group ? <Empty icon="shield" title={t("Pick a group to see its evidence")} /> : q.isLoading ? <Loading /> : q.data ? (
        <>
          <p className="muted">{t("{{ranch}} · records from {{from}} to {{to}} · made {{date}}", { ranch: q.data.ranch, from: formatDate(q.data.from), to: formatDate(q.data.to), date: formatDate(q.data.generated_at.slice(0, 10)) })}</p>
          <EvidenceView packet={q.data} />
        </>
      ) : <ErrorNote error={q.error} />}
    </Page>
  );
}

